/**
 * PLA-33 (R01-06): Roast Landing Page
 * Displays roast results and triggers OTP verification flow.
 */

import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { OtpVerification } from "@/components/campaign/OtpVerification";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface RoastData {
  text: string;
  source: string;
  canon_id: string | null;
  severity: number;
  format: Record<string, unknown>;
  created_at: string;
}

export default function RoastPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [campaignSessionToken, setCampaignSessionToken] = useState<string | null>(null);
  const [swingId, setSwingId] = useState<string | null>(null);
  const [roast, setRoast] = useState<RoastData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showOtp, setShowOtp] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Get campaign session token from URL or localStorage
    const tokenFromUrl = searchParams.get("token");
    const tokenFromStorage = localStorage.getItem("campaign_session_token");
    const token = tokenFromUrl || tokenFromStorage;

    // Get swing_id from URL
    const swingIdParam = searchParams.get("swing_id");

    if (!token) {
      setError("Missing campaign session token");
      setIsLoading(false);
      return;
    }

    if (!swingIdParam) {
      setError("Missing swing ID");
      setIsLoading(false);
      return;
    }

    setCampaignSessionToken(token);
    setSwingId(swingIdParam);

    // Fetch roast
    fetchRoast(token, swingIdParam);
  }, [searchParams]);

  const fetchRoast = async (token: string, swingId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch roast using campaign-swing-roast-get endpoint
      const supabaseUrl = "https://yhcymqfdigeufzooutxi.supabase.co";
      const url = `${supabaseUrl}/functions/v1/campaign-swing-roast-get?swing_id=${swingId}`;
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || "Failed to load roast");
      }

      if (data.roast) {
        setRoast(data.roast);
      }
    } catch (err) {
      console.error("[RoastPage] Fetch error:", err);
      setError(err instanceof Error ? err.message : "Failed to load roast");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCtaClick = async () => {
    if (!campaignSessionToken) {
      setError("Missing campaign session token");
      return;
    }

    // Check if user is already authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      // Already authenticated, redirect to home
      navigate("/home");
      return;
    }

    // Show OTP verification
    setShowOtp(true);
  };

  const handleOtpSuccess = () => {
    // Redirect to home after successful verification
    navigate("/home");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Loading your roast...</p>
        </div>
      </div>
    );
  }

  if (error && !roast) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </div>
      </div>
    );
  }

  if (showOtp && campaignSessionToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md">
          <OtpVerification
            campaignSessionToken={campaignSessionToken}
            onSuccess={handleOtpSuccess}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted p-4">
      <div className="max-w-2xl mx-auto pt-12">
        {roast && (
          <div className="bg-card rounded-lg shadow-lg p-8 mb-8">
            <div className="text-center mb-6">
              <h1 className="text-4xl font-bold mb-4">Your Roast</h1>
              <div className="bg-muted rounded-lg p-6 mb-6">
                <p className="text-2xl font-medium leading-relaxed">{roast.text}</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Source: {roast.source === "canon" ? "Classic Roast" : roast.source === "generated" ? "AI Generated" : "Fallback"}
              </p>
            </div>

            <div className="text-center">
              <Button
                onClick={handleCtaClick}
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                See Your Real Swing Analysis
              </Button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-destructive/10 text-destructive p-4 rounded-lg mb-4">
            <p>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}

